import { useId } from 'react'

interface ProgressBarProps {
  /** The group's accessible name, and the visible label. */
  label: string
  /**
   * 0–100, or `null` when how far along it is genuinely is not known.
   *
   * `null` renders an INDETERMINATE bar, which is the honest rendering of a job
   * that has been accepted and not yet started: `Job.progress_pct` defaults to 0
   * and a bar pinned at 0% says "nothing has happened", which is a claim, while
   * an indeterminate one says "something is happening and I cannot tell you how
   * much", which is the truth.
   */
  value: number | null
  /** The phase, or the byte count — whatever says more than the number does. */
  detail?: string
}

/**
 * A determinate progress indicator that also reads as text.
 *
 * `<progress>` rather than `role="progressbar"` on a div: the element carries
 * the role, the value and the indeterminate state natively, and omitting `value`
 * is the platform's own way of saying "unknown" — which a hand-rolled div can
 * only imitate with three ARIA attributes that have to agree with each other.
 *
 * The percentage is rendered as text beside the bar as well, because a bar alone
 * is a non-text indicator and §7.2 asks for non-colour encoding throughout. It is
 * also the only version of this a screen reader announces without the user having
 * to go looking for the control.
 *
 * **Both progress values here are real.** The upload's comes from the browser's
 * own byte counter and the job's from `Job.progress_pct`, which the backend
 * writes. Nothing on this bar is a denominator the client invented — which is
 * exactly why the provisional-baseline countdown is NOT a progress bar.
 */
export function ProgressBar({ label, value, detail }: ProgressBarProps) {
  const id = useId()
  const indeterminate = value === null

  return (
    <div className="progress">
      <div className="progress__head">
        <span className="progress__label" id={id}>
          {label}
        </span>
        {/* A separate class, not a colour swap: `working…` is a WORD where the
            determinate state shows a number, so it takes the mono voice
            instead of the display one. The words are unchanged. */}
        <span
          className={
            indeterminate ? 'progress__value progress__value--waiting' : 'progress__value'
          }
        >
          {indeterminate ? 'working…' : `${value}%`}
        </span>
      </div>
      <progress
        className="progress__bar"
        aria-labelledby={id}
        max={100}
        // `undefined`, not 0. A `<progress>` with no value attribute is the
        // indeterminate one; with `value={0}` it is a determinate bar asserting
        // that nothing has happened.
        value={indeterminate ? undefined : value}
      />
      {detail !== undefined && <p className="progress__detail">{detail}</p>}
    </div>
  )
}
